import type { ReportFilterClause } from '@eis/core/reporting';
import type { ReportDocument } from '../../../lib/document';

/**
 * The expression with every quoted field name in the dataset's own spelling. The field list shows
 * the column `vorname` as "Vorname", and an author types what they see; the warehouse (and the
 * service's check) compares exactly (owner feedback 2026-11-01). A name that matches no field, or
 * several fields ignoring case, is left as typed — the check names it. Text inside single-quoted
 * literals is never touched.
 */
export function canonicalExpression(expression: string, fields: readonly string[]): string {
  const exact = new Set(fields);
  const folded = new Map<string, string[]>();
  for (const name of fields) {
    const key = name.toLocaleLowerCase('de');
    folded.set(key, [...(folded.get(key) ?? []), name]);
  }
  let out = '';
  let i = 0;
  while (i < expression.length) {
    const c = expression.charAt(i);
    if (c === "'") {
      // A literal, with '' as an escaped quote.
      let j = i + 1;
      while (j < expression.length) {
        if (expression.charAt(j) === "'" && expression.charAt(j + 1) === "'") j += 2;
        else if (expression.charAt(j) === "'") break;
        else j += 1;
      }
      out += expression.slice(i, j + 1);
      i = j + 1;
      continue;
    }
    if (c === '"') {
      let j = i + 1;
      let name = '';
      while (j < expression.length) {
        if (expression.charAt(j) === '"' && expression.charAt(j + 1) === '"') {
          name += '"';
          j += 2;
        } else if (expression.charAt(j) === '"') break;
        else {
          name += expression.charAt(j);
          j += 1;
        }
      }
      const matches = folded.get(name.toLocaleLowerCase('de')) ?? [];
      const spelled = !exact.has(name) && matches.length === 1 ? (matches[0] ?? name) : name;
      out += `"${spelled.replace(/"/g, '""')}"`;
      i = j + 1;
      continue;
    }
    out += c;
    i += 1;
  }
  return out;
}

/** The UI-only range operator ("zwischen"): stored as `>= from` followed by `<= to`. */
export const RANGE_OP = '[]';

/** The filter dialog's state: a new filter, or the one at `index` (a range: `span` 2) edited. */
export interface PickerState {
  readonly field: string;
  readonly op: string;
  /** One value, the list's values, or `[from, to]` for the range. */
  readonly values: readonly string[];
  readonly editable: boolean;
  readonly label: string;
  readonly index?: number;
  /** How many clauses the edited filter occupies: 2 for a range, else 1. */
  readonly span?: number;
}

export const LIST_OPS: readonly string[] = ['=()', '!()'];
export const NO_VALUE_OPS: readonly string[] = ['==$null', '!=$null'];
const NUMBER = /^-?\d+(\.\d+)?$/;

/**
 * Where the range around clause `index` starts, if it is one: a `>=` followed by a `<=` on the
 * same field with the same editability. Ranges are two clauses so the SDD-017 operator set and the
 * service stay as they are; only the console shows the pair as one filter.
 */
export function rangeStart(
  clauses: readonly ReportFilterClause[],
  index: number,
): number | undefined {
  const pair = (i: number): boolean => {
    const a = clauses[i];
    const b = clauses[i + 1];
    return (
      a !== undefined &&
      b !== undefined &&
      a.op === '>=' &&
      b.op === '<=' &&
      a.field === b.field &&
      (a.editable === true) === (b.editable === true)
    );
  };
  if (pair(index)) return index;
  if (index > 0 && pair(index - 1)) return index - 1;
  return undefined;
}

/** A filter opened for editing (owner feedback 2026-11-01: an existing filter could not be edited). */
export function pickerOfClause(
  clauses: readonly ReportFilterClause[],
  index: number,
): PickerState | undefined {
  const start = rangeStart(clauses, index);
  if (start !== undefined) {
    const from = clauses[start];
    const to = clauses[start + 1];
    if (from === undefined || to === undefined) return undefined;
    return {
      field: from.field,
      op: RANGE_OP,
      values: [from.value ?? '', to.value ?? ''],
      editable: from.editable === true,
      label: from.label ?? '',
      index: start,
      span: 2,
    };
  }
  const clause = clauses[index];
  if (clause === undefined) return undefined;
  return {
    field: clause.field,
    op: clause.op,
    values: clause.values ?? (clause.value === undefined ? [] : [clause.value]),
    editable: clause.editable === true,
    label: clause.label ?? '',
    index,
    span: 1,
  };
}

export type MissingValue = 'valueRequired' | 'valuesRequired' | 'rangeRequired' | 'notANumber';

/**
 * The clause(s) the dialog describes, or the i18n key of what is missing — an empty "one of" is
 * stopped here, never by the schema's `minItems: 1` after the save (owner feedback 2026-11-01).
 */
export function clausesOfPicker(
  picker: PickerState,
  type: string | undefined,
): { clauses: ReportFilterClause[] } | { missing: MissingValue } {
  const common = {
    field: picker.field,
    ...(picker.editable ? { editable: true } : {}),
    ...(picker.label.trim() === '' ? {} : { label: picker.label.trim() }),
  };
  const clean = (v: string): string => (type === 'NUMBER' ? v.trim().replace(',', '.') : v.trim());
  const values = picker.values.map(clean).filter((v, i, all) => v !== '' && all.indexOf(v) === i);
  if (type === 'NUMBER' && values.some((v) => !NUMBER.test(v))) return { missing: 'notANumber' };
  const op = picker.op as ReportFilterClause['op'];
  if (NO_VALUE_OPS.includes(op)) return { clauses: [{ ...common, op }] };
  if (picker.op === RANGE_OP) {
    const from = clean(picker.values[0] ?? '');
    const to = clean(picker.values[1] ?? '');
    if (from === '' || to === '') return { missing: 'rangeRequired' };
    return {
      clauses: [
        { ...common, op: '>=', value: from },
        { ...common, op: '<=', value: to },
      ],
    };
  }
  if (LIST_OPS.includes(op)) {
    if (values.length === 0) return { missing: 'valuesRequired' };
    return { clauses: [{ ...common, op, values }] };
  }
  const value = values[0];
  if (value === undefined) return { missing: 'valueRequired' };
  return { clauses: [{ ...common, op, value }] };
}

/** Adds the clause(s), or replaces the `span` clauses at `index` with them. */
export function putClauses(
  doc: ReportDocument,
  next: readonly ReportFilterClause[],
  index: number | undefined,
  span = 1,
): ReportDocument {
  const filters = doc.filters ?? { join: 'ALL' as const, clauses: [] };
  const clauses =
    index === undefined
      ? [...filters.clauses, ...next]
      : [...filters.clauses.slice(0, index), ...next, ...filters.clauses.slice(index + span)];
  return { ...doc, filters: { ...filters, clauses } };
}

export interface SortKey {
  readonly by: string;
  readonly direction: 'ASC' | 'DESC';
}

/** The report's default sort: the table's, else the pivot's (a chart sorts its pivot). */
export function sortOf(doc: ReportDocument): readonly SortKey[] {
  return (doc.reportType === 'TABLE' ? doc.table?.sort : doc.pivot?.sort) ?? [];
}

/**
 * Sorts the report by a key first (or drops the key): the editor's "Aufsteigend / Absteigend
 * sortieren" on a token (owner feedback 2026-11-01). The report's other keys stay behind it (the
 * schema allows five). The viewer may override the sort per run.
 */
export function setSort(
  doc: ReportDocument,
  by: string,
  direction: 'ASC' | 'DESC' | undefined,
): ReportDocument {
  const others = sortOf(doc).filter((s) => s.by !== by);
  const sort: SortKey[] =
    direction === undefined ? [...others] : [{ by, direction }, ...others].slice(0, 5);
  if (doc.reportType === 'TABLE') {
    const { sort: _old, ...table } = doc.table ?? { columns: [] };
    return { ...doc, table: sort.length === 0 ? table : { ...table, sort } };
  }
  const { sort: _old, ...pivot } = doc.pivot ?? { values: [] };
  return { ...doc, pivot: sort.length === 0 ? pivot : { ...pivot, sort } };
}

/** Renames a calculated field everywhere the document names it (shelves, sort, filters, formats). */
export function renameField(doc: ReportDocument, from: string, to: string): ReportDocument {
  if (from === to) return doc;
  const walk = (node: unknown): unknown => {
    if (Array.isArray(node)) return node.map(walk);
    if (node === null || typeof node !== 'object') return node;
    return Object.fromEntries(
      Object.entries(node as Record<string, unknown>).map(([key, value]) => [
        key,
        (key === 'field' || key === 'by') && value === from ? to : walk(value),
      ]),
    );
  };
  const next = walk(doc) as ReportDocument;
  const format = next.formats?.[from];
  if (next.formats === undefined || format === undefined) return next;
  const formats = Object.fromEntries(
    Object.entries(next.formats).map(([key, value]) => [key === from ? to : key, value]),
  );
  return { ...next, formats };
}

/** True when the document uses the field anywhere but in its own calculated-field list. */
export function usesField(doc: ReportDocument, name: string): boolean {
  const { calculatedFields: _calcs, formats: _formats, ...rest } = doc;
  const needle = JSON.stringify(name);
  return (
    JSON.stringify(rest).includes(`"field":${needle}`) ||
    JSON.stringify(rest).includes(`"by":${needle}`)
  );
}
